import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon,
  EventNote as EventNoteIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  EventAvailable as EventAvailableIcon,
  QrCode as QrCodeIcon,
  PictureAsPdf as PdfIcon,
  DescriptionOutlined as FileIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { deleteQridRecord, QridRecord } from '../services/qridService';
import { getInvoiceRecordsByQrid, getInvoicesByQridAndType, InvoiceRecord, getRefundRecordsByQrid, RefundRecord } from '../services/invoiceService';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { shouldExcludeOldCompletedRecord } from '../utils/dashboardUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`qrid-tabpanel-${index}`}
      aria-labelledby={`qrid-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `qrid-tab-${index}`,
    'aria-controls': `qrid-tabpanel-${index}`,
  };
};

// Status chip color mapping
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'pending return':
      return 'warning';
    case 'pending packing':
      return 'info';
    case 'ready to dispatch':
      return 'secondary';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
};

// Get status style for custom chip styling
const getStatusStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return { bgcolor: '#4caf50', color: 'white' };
    case 'pending return':
      return { bgcolor: '#ff9800', color: 'white' };
    case 'pending packing':
      return { bgcolor: '#2196f3', color: 'white' };
    case 'ready to dispatch':
      return { bgcolor: '#9c27b0', color: 'white' };
    case 'canceled':
      return { bgcolor: '#f44336', color: 'white' };
    default:
      return { bgcolor: '#e0e0e0', color: 'text.primary' };
  }
};

// Format date for better display - handles string format like "22-03-2025"
const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  
  try {
    // Check if it's in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-').map(part => parseInt(part, 10));
      return `${day} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]} ${year}`;
    }
    
    // Fallback to standard date parsing
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric'
      });
    }
    
    // If all else fails, return the original string
    return dateString;
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return dateString;
  }
};

// Add a helper function to get invoice number from document
const getInvoiceNumber = (doc: any) => {
  // Check common invoice numbers we've seen in the logs
  const commonInvoiceNumbers = ["INV 2004143", "INV 2004173", "INV 2004179"];
  
  if (doc && typeof doc === 'object') {
    console.log("Getting invoice number from document:", doc);
    
    // Look for "Invoice No" field (as shown in the logs)
    if (doc["Invoice No"] !== undefined) {
      console.log("Found 'Invoice No' field with value:", doc["Invoice No"]);
      return doc["Invoice No"];
    }
    
    // Search for fields that might contain the invoice number pattern
    const invoicePattern = /INV\s+\d+/;  // Pattern like "INV 2004179"
    
    // Check all values in the document for the invoice number pattern
    for (const key in doc) {
      if (typeof doc[key] === 'string') {
        const value = doc[key];
        
        // Check direct match against known invoice numbers
        if (commonInvoiceNumbers.includes(value)) {
          console.log(`Found exact invoice number match in field '${key}': ${value}`);
          return value;
        }
        
        // Check if value matches the INV pattern
        const match = value.match(invoicePattern);
        if (match && match[0]) {
          console.log(`Found invoice number pattern match in field '${key}': ${match[0]}`);
          return match[0];
        }
      }
    }
    
    // Also check "Invoice No:" field (with colon)
    if (doc["Invoice No:"] !== undefined) {
      console.log("Found 'Invoice No:' field with value:", doc["Invoice No:"]);
      return doc["Invoice No:"];
    }
    
    // Check for any field containing both "invoice" and "no" in its name
    for (const key in doc) {
      if (key.toLowerCase().includes('invoice') && key.toLowerCase().includes('no')) {
        if (typeof doc[key] === 'string') {
          console.log(`Found field '${key}' with value:`, doc[key]);
          return doc[key];
        }
      }
    }
    
    // Last resort: check all string fields for any value starting with "INV"
    for (const key in doc) {
      if (typeof doc[key] === 'string' && doc[key].startsWith("INV")) {
        console.log(`Found field with INV prefix - '${key}': ${doc[key]}`);
        return doc[key];
      }
    }
  }
  
  console.log("No invoice number found in document");
  return null;
};

// Add a direct accessor function to get the invoice number from the record
const getDirectInvoiceNumber = (doc: any): string | null => {
  if (!doc) return null;
  
  console.log("Trying to extract invoice number directly from document:", doc);
  
  // Direct access to "Invoice No" field as confirmed in the console logs
  if (doc["Invoice No"] !== undefined) {
    console.log("Found 'Invoice No' field with value:", doc["Invoice No"]);
    return doc["Invoice No"];
  }
  
  // Check for other possible field names as fallbacks
  const possibleFields = ["Invoice_No", "invoice_no", "InvoiceNo", "Invoice No:"];
  for (const field of possibleFields) {
    if (doc[field] !== undefined && doc[field] !== null) {
      console.log(`Found invoice number in field '${field}':`, doc[field]);
      return doc[field];
    }
  }
  
  // Check for invoice number pattern in all fields
  for (const key in doc) {
    const value = doc[key];
    if (typeof value === 'string' && value.match(/INV\s+\d+/)) {
      console.log(`Found invoice number pattern in field ${key}:`, value);
      return value;
    }
  }
  
  console.log("No invoice number found in document");
  return null;
};

// Function to safely extract the invoice number
function extractInvoiceNumber(doc: any): string {
  if (!doc) return 'Unknown';
  
  // Directly log the document to inspect available fields
  console.log('EXTRACT INVOICE NUMBER FROM:', JSON.stringify(doc, null, 2));
  
  // Access the exact field using string index notation
  const invoiceNo = doc['Invoice No'] || doc['Invoice_No'];
  console.log('EXTRACTED INVOICE NO:', invoiceNo);
  
  return invoiceNo || 'Unknown';
}

// Add global cache to prevent refetching every time component mounts
const QR_RECORDS_CACHE_KEY = 'qr_records_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

interface GlobalCache {
  records: QridRecord[];
  totalRecords: number;
  timestamp: number;
}

// Function to get records from cache
const getRecordsFromCache = (): GlobalCache | null => {
  try {
    const cachedData = sessionStorage.getItem(QR_RECORDS_CACHE_KEY);
    if (!cachedData) return null;
    
    const parsedData = JSON.parse(cachedData) as GlobalCache;
    const now = Date.now();
    
    // Return cache if it's less than 5 minutes old
    if (now - parsedData.timestamp < CACHE_EXPIRY) {
      console.log(`Using cached QR records, age: ${(now - parsedData.timestamp)/1000}s`);
      return parsedData;
    }
    return null;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
};

// Function to save records to cache
const saveRecordsToCache = (records: QridRecord[], totalRecords: number) => {
  try {
    const cacheData: GlobalCache = {
      records,
      totalRecords,
      timestamp: Date.now()
    };
    sessionStorage.setItem(QR_RECORDS_CACHE_KEY, JSON.stringify(cacheData));
    console.log('QR records saved to cache');
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
};

// Add an interface for QridManagement props
interface QridManagementProps {
  onRecordSelect?: (record: QridRecord) => void;
}

const QridManagement: React.FC<QridManagementProps> = ({ onRecordSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [records, setRecords] = useState<QridRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<QridRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState<{ total: number; completed: number; pending: number }>({
    total: 0,
    completed: 0,
    pending: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState<QridRecord | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [detailRecord, setDetailRecord] = useState<QridRecord | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [invoiceRecords, setInvoiceRecords] = useState<InvoiceRecord[]>([]);
  const [guidanceRecords, setGuidanceRecords] = useState<InvoiceRecord[]>([]);
  const [refundRecords, setRefundRecords] = useState<RefundRecord[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [guidanceMenuAnchorEl, setGuidanceMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [refundMenuAnchorEl, setRefundMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fix the document cache type
  const [documentCache, setDocumentCache] = useState<{
    [qrid: string]: {
      invoices: InvoiceRecord[];
      guidance: InvoiceRecord[];
      refunds: RefundRecord[];
      timestamp: number;
    }
  }>({});

  // Wrap filterRecords in useCallback to prevent it from changing on every render
  const filterRecords = useCallback((recordsToFilter: QridRecord[], search: string, status: string) => {
    console.log(`Filtering records - Status filter: "${status}", Search term: "${search}"`);
    console.log(`Records to filter: ${recordsToFilter.length}`);
    
    let filtered = recordsToFilter.filter((record) => !shouldExcludeOldCompletedRecord(record));
    
    // Apply status filter
    if (status !== 'all') {
      const beforeCount = filtered.length;
      filtered = filtered.filter(record => 
        record.Status?.toLowerCase() === status.toLowerCase()
      );
      console.log(`After status filter: ${filtered.length} (removed ${beforeCount - filtered.length})`);
    }
    
    // Apply search filter (if search term exists)
    if (search.trim()) {
      const beforeCount = filtered.length;
      search = search.toLowerCase();
      filtered = filtered.filter(record => 
        (record.Name?.toLowerCase().includes(search)) ||
        (record.Contact_Number?.includes(search)) ||
        (record["Contact Number"]?.includes(search)) ||
        (record.OrderID?.toLowerCase().includes(search)) ||
        (record.Qrid?.includes(search))
      );
      console.log(`After search filter: ${filtered.length} (removed ${beforeCount - filtered.length})`);
    }
    
    console.log(`Final filtered records: ${filtered.length}`);
    setFilteredRecords(filtered);
  }, []);

  // Set up real-time listeners with pagination and limiting
  useEffect(() => {
    setLoading(true);
    
    // Load data from cache first if available
    const cachedData = getRecordsFromCache();
    if (cachedData) {
      const visibleCachedRecords = cachedData.records.filter((record) => !shouldExcludeOldCompletedRecord(record));
      setRecords(visibleCachedRecords);
      setTotalRecords(visibleCachedRecords.length);
      filterRecords(visibleCachedRecords, searchTerm, filterStatus);
      setLoading(false);
      console.log('Loaded QR records from cache, now setting up real-time listener');
    }
    
    // Listen for QridRecords in real-time with limit (reducing initial load time)
    const recordsQuery = query(
      collection(db, "QridRecord"),
      orderBy("Timestamp", "desc"),
      limit(100) // Limit to 100 most recent records to improve performance
    );
    
    const unsubscribeRecords = onSnapshot(recordsQuery, (querySnapshot) => {
      // Track all records from DB
      const allRecordsFromDB: QridRecord[] = [];
      
      // Map to track records by Qrid to prevent duplicates
      const qridMap: { [key: string]: QridRecord } = {};
      const orderIdMap: { [key: string]: string[] } = {};
      const recordsData: QridRecord[] = [];
      
      console.log(`Total documents received: ${querySnapshot.size}`);
      
      querySnapshot.forEach((doc) => {
        // Create a record object with all fields
        const data = doc.data();
        const record = { 
          id: doc.id, 
          ...data,
          // Ensure these fields are always accessible regardless of naming variations
          Name: data.Name,
          OrderID: data.OrderID,
          Contact_Number: data.Contact_Number || data["Contact Number"],
          Pickup_Date: data.Pickup_Date || data["Pickup Date"],
          Return_Date: data.Return_Date || data["Return Date"],
          Status: data.Status,
          Qrid: data.Qrid,
        } as QridRecord;

        if (shouldExcludeOldCompletedRecord(record)) {
          return;
        }
        
        allRecordsFromDB.push(record);
        
        const qrid = record.Qrid;
        const orderId = record.OrderID;
        
        if (qrid) {
          // Initialize arrays for collecting OrderIDs if this is the first time seeing this Qrid
          if (!orderIdMap[qrid]) {
            orderIdMap[qrid] = [];
          }
          
          // Add OrderID to the collection if it exists and is not already included
          if (orderId && !orderIdMap[qrid].includes(orderId)) {
            orderIdMap[qrid].push(orderId);
          }
          
          // If this is the first time seeing this Qrid or if this record has a newer timestamp,
          // use this record as the reference
          if (!qridMap[qrid] || 
              (record.Timestamp && 
               qridMap[qrid].Timestamp && 
               record.Timestamp.toDate() > qridMap[qrid].Timestamp.toDate())) {
            
            // Creating a new record to avoid reference issues
            qridMap[qrid] = { ...record };
          }
        } else {
          // If no Qrid, just add as a normal record
          recordsData.push(record);
        }
      });
      
      console.log(`Records with unique QR IDs: ${Object.keys(qridMap).length}`);
      console.log(`Records without QR ID: ${recordsData.length}`);
      
      // Update OrderIDs to the combined string for each Qrid
      Object.keys(qridMap).forEach(qrid => {
        if (orderIdMap[qrid].length > 0) {
          qridMap[qrid].OrderID = orderIdMap[qrid].join(', ');
        }
        recordsData.push(qridMap[qrid]);
      });
      
      console.log(`Total consolidated records: ${recordsData.length}`);
      setRecords(recordsData);
      const completedCount = recordsData.filter(
        (record) => (record.Status || '').toLowerCase() === 'completed'
      ).length;
      const pendingCount = recordsData.filter(
        (record) => (record.Status || '').toLowerCase() === 'pending return'
      ).length;
      setStats({
        total: recordsData.length,
        completed: completedCount,
        pending: pendingCount
      });
      // Save the records to session cache for future visits
      saveRecordsToCache(recordsData, recordsData.length);
      filterRecords(recordsData, searchTerm, filterStatus);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to QridRecords:", error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch data in real-time',
        severity: 'error'
      });
      setLoading(false);
    });
    
    // Cleanup function
    return () => {
      unsubscribeRecords();
    };
  }, [filterRecords, searchTerm, filterStatus]); // Add filterRecords, searchTerm, and filterStatus as dependencies

  // Apply filters when search term or filter status changes
  useEffect(() => {
    filterRecords(records, searchTerm, filterStatus);
  }, [searchTerm, filterStatus, records, filterRecords]); // Add filterRecords as dependency

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setFilterStatus(event.target.value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (record: QridRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const confirmDelete = async () => {
    if (!selectedRecord) return;
    
    try {
      await deleteQridRecord(selectedRecord.id);
      
      setSnackbar({
        open: true,
        message: 'Record deleted successfully',
        severity: 'success'
      });
      
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting record:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete record',
        severity: 'error'
      });
    }
  };

  const handleViewDetails = (record: QridRecord) => {
    setDetailRecord(record);
    setDetailDialogOpen(true);
    
    if (record.Qrid) {
      setLoadingPdfs(true);
      setLoadingRefunds(true);
      console.log(`QridManagement: Fetching documents for QR record:`, record);
      
      // Check if we have cached data and it's less than 5 minutes old
      const qridString = String(record.Qrid); // Convert to string explicitly
      const cachedData = documentCache[qridString];
      const now = Date.now();
      const cacheValidFor = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      if (cachedData && (now - cachedData.timestamp < cacheValidFor)) {
        console.log(`Using cached data for Qrid ${qridString}, age: ${(now - cachedData.timestamp)/1000}s`);
        // Use cached data
        setInvoiceRecords(cachedData.invoices);
        setGuidanceRecords(cachedData.guidance);
        setRefundRecords(cachedData.refunds);
        setLoadingPdfs(false);
        setLoadingRefunds(false);
        return;
      }
      
      // If no valid cache, fetch all document types in parallel with proper typing
      Promise.all([
        getInvoicesByQridAndType(qridString, 'invoice'),
        getInvoicesByQridAndType(qridString, 'guidance'),
        getRefundRecordsByQrid(qridString)
      ]).then(results => {
        const invoices = results[0];
        const guidance = results[1];
        const refunds = results[2];
        
        // Update state with fetched data
        setInvoiceRecords(invoices);
        setGuidanceRecords(guidance);
        setRefundRecords(refunds);
        
        console.log(`Fetched ${invoices.length} invoice documents`);
        console.log(`Fetched ${guidance.length} guidance documents`);
        console.log(`Fetched ${refunds.length} refund documents`);
        
        // Update cache with properly typed values
        const newCacheEntry = {
          invoices,
          guidance,
          refunds,
          timestamp: Date.now()
        };
        
        setDocumentCache(prevCache => {
          const updatedCache = { ...prevCache };
          updatedCache[qridString] = newCacheEntry;
          return updatedCache;
        });
        
        console.log('All document fetches completed successfully');
      }).catch(err => {
        console.error('Error fetching documents:', err);
      }).finally(() => {
        setLoadingPdfs(false);
        setLoadingRefunds(false);
      });
    }
  };
  
  const handleOpenPdf = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };
  
  const handleInvoiceClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleGuidanceClick = (event: React.MouseEvent<HTMLElement>) => {
    setGuidanceMenuAnchorEl(event.currentTarget);
  };
  
  const handleRefundClick = (event: React.MouseEvent<HTMLElement>) => {
    setRefundMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };
  
  const handleCloseGuidanceMenu = () => {
    setGuidanceMenuAnchorEl(null);
  };
  
  const handleCloseRefundMenu = () => {
    setRefundMenuAnchorEl(null);
  };
  
  // Update the closeDetailDialog function to maintain cache
  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
    setDetailRecord(null);
    setInvoiceRecords([]);
    setGuidanceRecords([]);
    setRefundRecords([]);
    setMenuAnchorEl(null);
    setGuidanceMenuAnchorEl(null);
    setRefundMenuAnchorEl(null);
    // Note: We don't clear the cache here to maintain performance for reopens
  };

  // Add a cleanup function for the cache
  useEffect(() => {
    // Cleanup function to clear the cache every hour
    const interval = setInterval(() => {
      console.log('Clearing document cache');
      setDocumentCache({});
    }, 60 * 60 * 1000); // 1 hour
    
    return () => clearInterval(interval);
  }, []);

  // Initialize component with cache if available
  useEffect(() => {
    const cachedData = getRecordsFromCache();
    if (cachedData) {
      const visibleCachedRecords = cachedData.records.filter((record) => !shouldExcludeOldCompletedRecord(record));
      setRecords(visibleCachedRecords);
      setTotalRecords(visibleCachedRecords.length);
      setFilteredRecords(visibleCachedRecords);
      setLoading(false);
      setIsInitialLoad(false);
      console.log('Initialized QR records from cache');
    } else {
      // Will fetch from Firestore via the real-time listener
      console.log('No valid cache found, will fetch QR records from Firestore');
    }
  }, []);

  // Render different table layouts for mobile and desktop
  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    
    if (filteredRecords.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            <Box sx={{ py: 3 }}>
              <Typography variant="body1" color="textSecondary">No records found</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    
    return filteredRecords.map((record) => (
      <TableRow key={record.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewDetails(record)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: '#e3f2fd', mr: 2, width: 36, height: 36 }}>
              <QrCodeIcon fontSize="small" color="primary" />
            </Avatar>
            <Box sx={{ maxWidth: '85%' }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 'medium' }}>
                {record.Name || 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap>
                {record.OrderID || 'No Order ID'}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap sx={{ fontWeight: 'medium' }}>
            {record.Qrid || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventNoteIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main', fontSize: '0.9rem' }} />
            <Typography variant="body2">{formatDate(record.Pickup_Date || record["Pickup Date"])}</Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'secondary.main', fontSize: '0.9rem' }} />
            <Typography variant="body2">{formatDate(record.Return_Date || record["Return Date"])}</Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.9rem' }} />
            <Typography variant="body2">{record.Contact_Number || record["Contact Number"] || 'N/A'}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip 
            label={record.Status || 'N/A'} 
            size="small"
            sx={{ 
              ...getStatusStyle(record.Status || ''),
              fontWeight: 500, 
              minWidth: 100, 
              borderRadius: '16px'
            }}
          />
        </TableCell>
        <TableCell align="right">
          <IconButton 
            size="small" 
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(record);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <Box sx={{ width: '100%', maxWidth: '1100px' }}>
        <Paper elevation={3} sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? "fullWidth" : "standard"}
              aria-label="qrid management tabs"
            >
              <Tab 
                icon={<EventNoteIcon />} 
                label="Overview" 
                {...a11yProps(0)} 
                iconPosition="start" 
              />
              <Tab 
                icon={<QrCodeIcon />} 
                label="QR Records" 
                {...a11yProps(1)} 
                iconPosition="start" 
              />
            </Tabs>
          </Box>

          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>
              QR ID Records Dashboard
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Overview of all QR ID records and statistics.
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                    <QrCodeIcon color="primary" sx={{ fontSize: { xs: 32, md: 40 }, mb: 1 }} />
                    <Typography variant="h4">{stats.total}</Typography>
                    <Typography variant="body1" color="text.secondary">Total Records</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                    <EventAvailableIcon color="success" sx={{ fontSize: { xs: 32, md: 40 }, mb: 1 }} />
                    <Typography variant="h4">{stats.completed}</Typography>
                    <Typography variant="body1" color="text.secondary">Completed</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                    <AccessTimeIcon color="warning" sx={{ fontSize: { xs: 32, md: 40 }, mb: 1 }} />
                    <Typography variant="h4">{stats.pending}</Typography>
                    <Typography variant="body1" color="text.secondary">Pending</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: { xs: 2, md: 4 } }} />
            
            <Typography variant="h6" gutterBottom>
              Recent Records
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowX: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="25%">Name</TableCell>
                      {!isMobile && <TableCell width="25%">Order ID</TableCell>}
                      {!isMobile && <TableCell width="15%">Status</TableCell>}
                      <TableCell width="17.5%">Pickup Date</TableCell>
                      <TableCell width="17.5%">Return Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.slice(0, 5).map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.Name || 'N/A'}</TableCell>
                        {!isMobile && <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.OrderID || 'N/A'}</TableCell>}
                        {!isMobile && (
                          <TableCell>
                            <Chip 
                              label={record.Status || 'Unknown'} 
                              color={getStatusColor(record.Status || '') as any}
                              size="small"
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.Pickup_Date || record["Pickup Date"] || 'N/A'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.Return_Date || record["Return Date"] || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setTabValue(1)}
                endIcon={<QrCodeIcon />}
              >
                View All Records
              </Button>
            </Box>
          </TabPanel>

          {/* QR Records Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Search by name or contact number"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={filterStatus}
                    label="Status"
                    onChange={handleStatusFilterChange}
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Pending Return">Pending Return</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  maxHeight: 550,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  '& .MuiTableCell-root': {
                    px: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              >
                <Table 
                  stickyHeader 
                  size={isMobile ? "small" : "medium"} 
                  sx={{ 
                    minWidth: 900,
                    tableLayout: 'fixed'
                  }}
                >
                  {isMobile ? (
                    <>
                      <TableBody>
                        {renderTableContent()}
                      </TableBody>
                    </>
                  ) : (
                    <>
                      <TableHead>
                        <TableRow>
                          <TableCell width="22%" sx={{ fontWeight: 'bold' }}>Name</TableCell>
                          <TableCell width="25%" sx={{ fontWeight: 'bold' }}>QR ID</TableCell>
                          <TableCell width="13%" sx={{ fontWeight: 'bold' }}>Pickup Date</TableCell>
                          <TableCell width="13%" sx={{ fontWeight: 'bold' }}>Return Date</TableCell>
                          <TableCell width="12%" sx={{ fontWeight: 'bold' }}>Contact Number</TableCell>
                          <TableCell width="10%" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell width="5%" align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {renderTableContent()}
                      </TableBody>
                    </>
                  )}
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle>Delete Record</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={closeDetailDialog} maxWidth="md" fullWidth>
        {detailRecord && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                QR Record Details
                <Chip 
                  label={detailRecord.Status || 'N/A'} 
                  size="small"
                  sx={{
                    ...getStatusStyle(detailRecord.Status || ''),
                    borderRadius: '16px',
                    fontWeight: 500
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Customer Information
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Name
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {detailRecord.Name || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Contact Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {detailRecord.Contact_Number || detailRecord["Contact Number"] || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Order ID
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {detailRecord.OrderID || 'N/A'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Rental Information
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          QR ID
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {detailRecord.Qrid || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Pickup Date
                        </Typography>
                        <Typography variant="body1" gutterBottom color="primary.main">
                          {formatDate(detailRecord.Pickup_Date || detailRecord["Pickup Date"])}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Return Date
                        </Typography>
                        <Typography variant="body1" gutterBottom color="secondary.main">
                          {formatDate(detailRecord.Return_Date || detailRecord["Return Date"])}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Documents
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<PdfIcon />}
                          onClick={handleInvoiceClick}
                          disabled={loadingPdfs || invoiceRecords.length === 0}
                          sx={{ borderRadius: 2 }}
                          endIcon={invoiceRecords.length > 1 ? <ArrowDropDownIcon /> : null}
                        >
                          {loadingPdfs ? 'LOADING...' : 
                           invoiceRecords.length === 0 ? 'NO INVOICES' : 
                           invoiceRecords.length === 1 ? extractInvoiceNumber(invoiceRecords[0]) : 
                           `${invoiceRecords.length} INVOICES`}
                        </Button>
                        
                        {/* Menu for invoice PDFs */}
                        <Menu
                          anchorEl={menuAnchorEl}
                          open={Boolean(menuAnchorEl)}
                          onClose={handleCloseMenu}
                        >
                          {invoiceRecords.map((invoice, index) => {
                            const invoiceNoDisplayValue = extractInvoiceNumber(invoice);
                            
                            return (
                              <MenuItem 
                                key={invoice.id} 
                                onClick={() => {
                                  handleOpenPdf(invoice.shortenLink);
                                  handleCloseMenu();
                                }}
                              >
                                <ListItemIcon>
                                  <PdfIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                <Typography variant="body2" fontWeight="medium">
                                  {invoiceNoDisplayValue}
                                </Typography>
                              </MenuItem>
                            );
                          })}
                        </Menu>
                        
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FileIcon />}
                          onClick={handleGuidanceClick}
                          disabled={loadingPdfs || guidanceRecords.length === 0}
                          sx={{ borderRadius: 2 }}
                          endIcon={guidanceRecords.length > 1 ? <ArrowDropDownIcon /> : null}
                        >
                          {loadingPdfs ? 'LOADING...' : 
                           guidanceRecords.length === 0 ? 'NO GUIDANCE' : 
                           guidanceRecords.length === 1 ? extractInvoiceNumber(guidanceRecords[0]) : 
                           `${guidanceRecords.length} GUIDANCE PDFS`}
                        </Button>
                        
                        {/* Menu for guidance PDFs */}
                        <Menu
                          anchorEl={guidanceMenuAnchorEl}
                          open={Boolean(guidanceMenuAnchorEl)}
                          onClose={handleCloseGuidanceMenu}
                        >
                          {guidanceRecords.map((guidance, index) => {
                            const invoiceNoDisplayValue = extractInvoiceNumber(guidance);
                            
                            return (
                              <MenuItem 
                                key={guidance.id} 
                                onClick={() => {
                                  handleOpenPdf(guidance.shortenLink);
                                  handleCloseGuidanceMenu();
                                }}
                              >
                                <ListItemIcon>
                                  <FileIcon fontSize="small" color="secondary" />
                                </ListItemIcon>
                                <Typography variant="body2" fontWeight="medium">
                                  {invoiceNoDisplayValue}
                                </Typography>
                              </MenuItem>
                            );
                          })}
                        </Menu>

                        {/* New Refund Receipt Button */}
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<ReceiptIcon />}
                          onClick={handleRefundClick}
                          disabled={loadingRefunds || refundRecords.length === 0}
                          sx={{ borderRadius: 2 }}
                          endIcon={refundRecords.length > 1 ? <ArrowDropDownIcon /> : null}
                        >
                          {loadingRefunds ? 'LOADING...' : 
                           refundRecords.length === 0 ? 'NO REFUNDS' : 
                           refundRecords.length === 1 ? `REFUND ${refundRecords[0].Invoice_No || ''}` : 
                           `${refundRecords.length} REFUNDS`}
                        </Button>
                        
                        {/* Menu for refund PDFs */}
                        <Menu
                          anchorEl={refundMenuAnchorEl}
                          open={Boolean(refundMenuAnchorEl)}
                          onClose={handleCloseRefundMenu}
                        >
                          {refundRecords.map((refund, index) => (
                            <MenuItem 
                              key={refund.id} 
                              onClick={() => {
                                handleOpenPdf(refund.permanentLink || refund.shortenLink || '');
                                handleCloseRefundMenu();
                              }}
                              disabled={!refund.permanentLink && !refund.shortenLink}
                            >
                              <ListItemIcon>
                                <ReceiptIcon fontSize="small" color="error" />
                              </ListItemIcon>
                              <Typography variant="body2" fontWeight="medium">
                                {refund.Invoice_No || `Refund ${index + 1}`}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Menu>
                        
                        {!invoiceRecords.length && !guidanceRecords.length && !refundRecords.length && !loadingPdfs && !loadingRefunds && (
                          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                            No documents available for this record
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDetailDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QridManagement; 
